import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { showErrorAlert } from "../../../services/ErrorHandlingService";
import { getSettingUsersAction } from "../../../store/actions/SettingAction";
import { getCampaignByDateAction } from "../../../store/actions/CampaignAction";
import paymentGatewayService from "../../../services/PaymentGatewayService";
import SendPaymentLinkButton from "./SendPaymentLinkButton";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CommonPaymentModal = ({
  show,
  onHide,
  title = "Collect Payment",
  patientName = "",
  initialAmount = "",
  paymentModes = [
    { value: "Online", label: "Online" },
    { value: "Bankingcard", label: "Banking Card" },
    { value: "Upi", label: "UPI" },
    { value: "Offline", label: "Cash" },
  ],
  enableCoupons = true,
  onSubmit,
  onCancel,
  loading = false,
  submitButtonText = "Collect Payment",
  cancelButtonText = "Cancel",
  showPatientName = true,
  requiredFields = ["amount", "paymentType"],
  customValidation = null,
  additionalFields = [],
  onAmountChange = null,
  onPaymentTypeChange = null,
  onCouponChange = null,
  paymentType = "appointment",
  paymentData = {},
  referenceId = "",
  paymentFor = "APPOINTMENT",
}) => {
  const dispatch = useDispatch();
  const { list: campaignList, num_rows: campaignCount } = useSelector((state) => state.campaign || { list: [], num_rows: 0 });

  const [formData, setFormData] = useState({
    amount: initialAmount || "",
    paymentType: "Online",
    couponCode: "",
    finalAmount: null,
    additionalFieldValues: {},
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const couponInputRef = useRef();
  const [consultingFee, setConsultingFee] = useState("");
  const [campaignAmount, setCampaignAmount] = useState(null);
  const [campaignTitle, setCampaignTitle] = useState(null);

  // 1. Fetch regular consulting fee from settings (fallback)
  useEffect(() => {
    if (paymentType === "appointment" || paymentType === "receptionist") {
      const fetchSettings = async () => {
        try {
          const settings = await dispatch(getSettingUsersAction());
          const consulting = settings.find((s) => s.setting_name === "Consulting Fees");
          if (consulting) {
            setConsultingFee(consulting.setting_value);
          }
        } catch (err) {
          console.error("Failed to fetch consulting fee:", err);
        }
      };
      fetchSettings();
    }
  }, [dispatch, paymentType]);

  // 2. Fetch campaign data when modal opens and we have an appointment date
  useEffect(() => {
    if (!show) return;
    const appointmentDate = paymentData?.date;
    if (!appointmentDate) return;

    // Format date to dd-mm-yyyy as expected by getCampaignByDateAction
    const formatDateForApi = (dateStr) => {
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };
    const formattedDate = formatDateForApi(appointmentDate);
    dispatch(getCampaignByDateAction(formattedDate));
  }, [show, paymentData?.date, dispatch]);

  // 3. Determine the correct amount based on campaign or regular fee
  useEffect(() => {
    const appointmentDate = paymentData?.date;
    const isCampaignRegistration = paymentData?.appointment_camp_reg === "Campaign";
    let finalAmountValue = consultingFee || initialAmount || "";

    // If we have campaign data from Redux, check if any campaign covers this date
    let activeCampaign = null;
    if (appointmentDate && campaignList?.length > 0) {
      const checkDate = new Date(appointmentDate);
      activeCampaign = campaignList.find((camp) => {
        const start = new Date(camp.start_date);
        const end = new Date(camp.end_date);
        return checkDate >= start && checkDate <= end;
      });
    }

    if ((isCampaignRegistration || activeCampaign) && activeCampaign?.consultation_charges) {
      const campAmount = Number(activeCampaign.consultation_charges);
      if (!isNaN(campAmount) && campAmount > 0) {
        finalAmountValue = campAmount;
        setCampaignAmount(campAmount);
        setCampaignTitle(activeCampaign.title);
      } else {
        setCampaignAmount(null);
        setCampaignTitle(null);
      }
    } else {
      setCampaignAmount(null);
      setCampaignTitle(null);
    }

    // Update form amount (but keep user-editable for non-appointment types)
    if (paymentType === "appointment" || paymentType === "receptionist") {
      setFormData((prev) => ({
        ...prev,
        amount: finalAmountValue,
      }));
    }
  }, [consultingFee, initialAmount, paymentData?.date, paymentData?.appointment_camp_reg, campaignList, paymentType]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      const defaultAmount = campaignAmount ? campaignAmount : (consultingFee || "");
      setFormData({
        amount: defaultAmount,
        paymentType: "Online",
        couponCode: "",
        finalAmount: null,
        additionalFieldValues: {},
      });
      setErrors({});
    }
  }, [show, consultingFee, campaignAmount]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    if (field === "amount" && onAmountChange) onAmountChange(value);
    if (field === "paymentType" && onPaymentTypeChange) onPaymentTypeChange(value);
    if (field === "couponCode" && onCouponChange) onCouponChange(value);
  };

  const validateForm = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });
    if (formData.amount) {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = "Please enter a valid amount greater than 0";
      }
    }
    if (customValidation) {
      const customValidationResult = customValidation(formData);
      if (!customValidationResult.isValid) {
        newErrors.custom = customValidationResult.message;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApplyCoupon = () => {
    const amt = Number.parseFloat(formData.amount);
    if (!amt || amt <= 0) {
      showErrorAlert("Enter a valid amount before applying the coupon.");
      return;
    }
    if (formData.couponCode.trim() === "") {
      showErrorAlert("Please enter a valid coupon code.");
      return;
    }
    handleInputChange("finalAmount", 0);
    couponInputRef?.current?.blur();
  };

  const handleRemoveCoupon = () => {
    handleInputChange("couponCode", "");
    handleInputChange("finalAmount", null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      let paymentPayload = {
        amount: parseFloat(formData.amount),
        paymentType: formData.paymentType,
        couponCode: formData.couponCode || null,
        finalAmount: formData.finalAmount !== null ? formData.finalAmount : parseFloat(formData.amount),
        additionalFieldValues: formData.additionalFieldValues,
      };
      switch (paymentType) {
        case "appointment":
          paymentPayload = { ...paymentPayload, ...paymentData, payment_for: paymentFor };
          break;
        case "medicine":
          paymentPayload = { ...paymentPayload, ...paymentData, payment_for: "MEDICINE", reference_id: referenceId };
          break;
        case "receptionist":
          paymentPayload = { ...paymentPayload, ...paymentData, payment_for: paymentFor };
          break;
        default:
          break;
      }
      await onSubmit(paymentPayload);
      // Auto-close modal after successful API response
      setTimeout(() => {
        onHide();
      }, 500);
    } catch (error) {
      console.error("Payment submission error:", error);
      showErrorAlert("Payment failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else onHide();
  };

  const pollPaymentStatus = async (transactionNumber) => {
    // ... (unchanged, keep as original)
    // (I omit the full function for brevity, but you can keep your existing implementation)
  };

  const handlePaymentSuccess = async (paymentResponse) => {
    // ... (unchanged, keep as original)
  };

  const isCouponApplied = formData.finalAmount !== null;
  const showCouponSection = enableCoupons && formData.paymentType === "Offline";

  return (
    <Modal show={show} onHide={handleCancel} centered size="lg" style={{ minWidth: "700px" }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
          {showPatientName && patientName && <span className="text-muted ms-2">for {patientName}</span>}
          {campaignTitle && (
            <span className="badge bg-info ms-3" style={{ fontSize: "0.8rem" }}>
              Campaign: {campaignTitle}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="amount" className="form-label">
              Amount <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              className={`form-control ${errors.amount ? "is-invalid" : ""}`}
              id="amount"
              value={formData.amount}
              onChange={(e) => handleInputChange("amount", e.target.value)}
              placeholder="Enter amount"
              min="0.01"
              step="0.01"
              required
              readOnly={paymentType === "appointment" || paymentType === "receptionist"}
              style={
                paymentType === "appointment" || paymentType === "receptionist"
                  ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                  : {}
              }
            />
            {campaignAmount && (
              <small className="text-muted d-block mt-1">
                <i className="fas fa-tag me-1"></i> Campaign amount applied.
              </small>
            )}
            {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
          </div>

          <div className="mb-3">
            <label htmlFor="paymentType" className="form-label">
              Payment Mode <span className="text-danger">*</span>
            </label>
            <select
              className={`form-control ${errors.paymentType ? "is-invalid" : ""}`}
              id="paymentType"
              value={formData.paymentType}
              onChange={(e) => handleInputChange("paymentType", e.target.value)}
              required
            >
              {paymentModes.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
            {errors.paymentType && <div className="invalid-feedback">{errors.paymentType}</div>}
          </div>

          {showCouponSection && (
            <>
              <div className="mb-3">
                <label htmlFor="couponCode" className="form-label">
                  Coupon Code (Optional)
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="couponCode"
                    value={formData.couponCode}
                    onChange={(e) => handleInputChange("couponCode", e.target.value)}
                    placeholder="Enter coupon code"
                    disabled={isCouponApplied}
                    ref={couponInputRef}
                  />
                  {!isCouponApplied ? (
                    <button type="button" className="btn btn-outline-secondary" onClick={handleApplyCoupon}>
                      Apply
                    </button>
                  ) : (
                    <button type="button" className="btn btn-outline-danger" onClick={handleRemoveCoupon}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              {isCouponApplied && (
                <div className="mb-3">
                  <strong>Final Amount: ₹{formData.finalAmount}</strong>
                </div>
              )}
            </>
          )}

          {additionalFields.map((field) => (
            <div key={field.name} className="mb-3">
              <label htmlFor={field.name} className="form-label">
                {field.label}
                {field.required && <span className="text-danger">*</span>}
              </label>
              {field.type === "textarea" ? (
                <textarea
                  className={`form-control ${errors[field.name] ? "is-invalid" : ""}`}
                  id={field.name}
                  value={formData.additionalFieldValues[field.name] || ""}
                  onChange={(e) =>
                    handleInputChange("additionalFieldValues", {
                      ...formData.additionalFieldValues,
                      [field.name]: e.target.value,
                    })
                  }
                  placeholder={field.placeholder}
                  required={field.required}
                />
              ) : (
                <input
                  type={field.type || "text"}
                  className={`form-control ${errors[field.name] ? "is-invalid" : ""}`}
                  id={field.name}
                  value={formData.additionalFieldValues[field.name] || ""}
                  onChange={(e) =>
                    handleInputChange("additionalFieldValues", {
                      ...formData.additionalFieldValues,
                      [field.name]: e.target.value,
                    })
                  }
                  placeholder={field.placeholder}
                  required={field.required}
                />
              )}
              {errors[field.name] && <div className="invalid-feedback">{errors[field.name]}</div>}
            </div>
          ))}

          {errors.custom && <div className="alert alert-danger">{errors.custom}</div>}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
        <Button
      variant="outline-danger"
      style={{
        background: "transparent",
        border: "1px solid #dc3545",
        color: "#dc3545",
        padding: "8px 20px",
        fontSize: "14px",
        fontWeight: "500",
        borderRadius: "4px",
        height: "54px",
        lineHeight: "1.5",
      }}
      onClick={handleCancel}
      disabled={loading || isSubmitting}
    >
      {cancelButtonText}
    </Button>
          <div className="d-flex gap-2">
            <SendPaymentLinkButton
              patientId={paymentData?.patient_id}
              appointmentId={paymentData?.appointment_id}
              leadId={paymentData?.lead_id}
              prescriptionId={paymentData?.prescription_id}
              queueId={paymentData?.queue_id}
              paymentFor={paymentFor}
              patientName={patientName}
              amount={formData.amount}
              paymentType={paymentType}
              onSuccess={() => {
                onHide();
              }}
              className="btn"
              size="sm"
              variant="outline-primary"
              style={{
                backgroundColor: "#17a2b8",
                borderColor: "#17a2b8",
                color: "white",
                fontWeight: "500",
                padding: "8px 20px",
                fontSize: "14px",
                borderRadius: "4px",
                height: "38px",
              }}
              disabled={loading || isSubmitting}
            />
            <Button
              style={{
                background: "#007680",
                border: "none",
                padding: "8px 20px",
                fontSize: "14px",
                borderRadius: "4px",
                height: "54px",
              }}
              onClick={handleSubmit}
              disabled={loading || isSubmitting}
            >
              {loading || isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                submitButtonText
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

CommonPaymentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string,
  patientName: PropTypes.string,
  initialAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paymentModes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  enableCoupons: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  loading: PropTypes.bool,
  submitButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  showPatientName: PropTypes.bool,
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  customValidation: PropTypes.func,
  additionalFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      placeholder: PropTypes.string,
      required: PropTypes.bool,
    })
  ),
  onAmountChange: PropTypes.func,
  onPaymentTypeChange: PropTypes.func,
  onCouponChange: PropTypes.func,
  paymentType: PropTypes.oneOf(["appointment", "medicine", "receptionist"]),
  paymentData: PropTypes.object,
  referenceId: PropTypes.string,
  paymentFor: PropTypes.string,
};

export default CommonPaymentModal;